import { z } from 'zod';

/**
 * AGENT_SPANS (runner → server), OTEL_TRACES_V1: the finished spans of one turn whose
 * USER_MESSAGE carried a sampled traceparent. The box never exports; the server checks the
 * trace is this task's own, records the spans and sends them on. Never forwarded to the web.
 *
 * The box runs the person's code, so the frame is bounded twice: by shape here (at most
 * MAX_SPANS_PER_FRAME spans, at most MAX_ATTRIBUTES_PER_SPAN short attributes each), and by
 * the CATALOG below on the server, which keeps only span names and attribute keys (with their
 * types) that the runner's own producer (apps/sandbox/src/tracing/turn-spans.ts) writes. Exact
 * membership, never a pattern: anything else is dropped and counted.
 */
export const MAX_SPANS_PER_FRAME = 200;
export const MAX_ATTRIBUTES_PER_SPAN = 16;

/** Every span name a runner sends. Low-cardinality on purpose: the round, lane, tool and hook ride attributes. */
export const RUNNER_SPAN_NAMES = ['vestra.runner_turn', 'vestra.model_call', 'vestra.tool', 'vestra.hook'] as const;
export type RunnerSpanName = (typeof RUNNER_SPAN_NAMES)[number];

/**
 * Every attribute key a runner sends, with its type. Never a task or trace id: the server
 * knows which task a frame is for, and a box must not be able to point a span at another one.
 */
export const RUNNER_SPAN_ATTRIBUTES = {
  'vestra.kickoff.gate_wait_ms': 'number',
  'vestra.kickoff.abilities_ms': 'number',
  'vestra.kickoff.journal_ms': 'number',
  'vestra.kickoff.tools_ms': 'number',
  'vestra.kickoff.prologue_ms': 'number',
  'vestra.kickoff.cold': 'boolean',
  'vestra.end_reason': 'string',
  'vestra.stop_reason': 'string',
  'vestra.lane': 'string',
  'vestra.lane_status': 'string',
  'vestra.round': 'number',
  'vestra.round_kind': 'string',
  'gen_ai.response.model': 'string',
  'gen_ai.usage.input_tokens': 'number',
  'gen_ai.usage.output_tokens': 'number',
  'vestra.cached_tokens': 'number',
  'vestra.ttft_ms': 'number',
  'vestra.first_text_ms': 'number',
  'vestra.inference_ms': 'number',
  'vestra.overhead_ms': 'number',
  'tool.name': 'string',
  'vestra.tool_outcome': 'string',
  'vestra.hook_event': 'string',
  'vestra.hook_name': 'string',
  'vestra.hook_phase': 'string',
} as const satisfies Record<string, 'string' | 'number' | 'boolean'>;
export type RunnerSpanAttributeKey = keyof typeof RUNNER_SPAN_ATTRIBUTES;

const HEX16 = /^[0-9a-f]{16}$/;

const AttributeValueSchema = z.union([z.string().max(200), z.number().finite(), z.boolean()]);

/** The server also checks the counts before parsing (runner-spans.ts), so a hostile frame cannot make the parse expensive. */
const AttributesSchema = z
  .record(z.string().max(64), AttributeValueSchema)
  .refine((o) => Object.keys(o).length <= MAX_ATTRIBUTES_PER_SPAN, { message: `at most ${MAX_ATTRIBUTES_PER_SPAN} attributes` });

export const RunnerSpanSchema = z.object({
  spanId: z.string().regex(HEX16),
  parentSpanId: z.string().regex(HEX16),
  /** Shape only; the server keeps a span only when this is one of RUNNER_SPAN_NAMES. */
  name: z.string().min(1).max(64),
  /** Epoch milliseconds on the runner's clock (the server keeps only a sane window around its own). */
  startMs: z.number().finite(),
  endMs: z.number().finite(),
  /** 'error' for a failure, 'unset' when the outcome is unknown; 'ok' when absent. */
  status: z.enum(['ok', 'error', 'unset']).optional(),
  attributes: AttributesSchema.optional(),
});

export const AgentSpansPayloadSchema = z.object({
  taskId: z.string(),
  /** 32 hex: the trace the turn's traceparent named. */
  traceId: z.string().regex(/^[0-9a-f]{32}$/),
  spans: z.array(RunnerSpanSchema).max(MAX_SPANS_PER_FRAME),
});

export type RunnerSpan = z.infer<typeof RunnerSpanSchema>;
export type AgentSpansPayload = z.infer<typeof AgentSpansPayloadSchema>;
